import axiosInstance from "@/axios";

export const state = {
    companiesData: [], // Changed from empty object to empty array
    companiesLoading: false,
    companiesMsg: null,
    errorMsg: null,
    successMsg: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNext: false,
        hasPrev: false
    }
}

export const mutations = {
    SET_COMPANIES_DATA(state, companiesData) {
        state.companiesData = companiesData
    },
    SET_LOADING(state, companiesLoading) {
        state.companiesLoading = companiesLoading
    },
    SET_COMPANIES_MSG(state, companiesMsg) {
        state.companiesMsg = companiesMsg
    },
    SET_PAGINATION(state, pagination) {
        state.pagination = pagination
    },
    UPDATE_COMPANY(state, updateCompany) {
        // Update the company in companiesData if it exists
        if (Array.isArray(state.companiesData)) {
            const index = state.companiesData.findIndex(company => company.id === updateCompany.id);
            if (index !== -1) {
                state.companiesData[index] = { ...state.companiesData[index], ...updateCompany };
            }
        }
    },
    ADD_COMPANY(state, company) {
        if (Array.isArray(state.companiesData)) {
            state.companiesData.unshift(company);
        }
    },
    DELETE_COMPANY(state, companyId) {
        if (Array.isArray(state.companiesData)) {
            state.companiesData = state.companiesData.filter(company => company.id !== companyId);
        }
    },
    SET_ERROR_MSG(state, errorMsg) {
        state.errorMsg = errorMsg
    },
    SET_SUCCESS_MSG(state, successMsg) {
        state.successMsg = successMsg
    },
}

export const getters = {
    companiesData: state => state.companiesData,
    companiesLoading: state => state.companiesLoading,
    companiesMsg: state => state.companiesMsg,
    companiesPagination: state => state.pagination,
    errorMsg: state => state.errorMsg,
    successMsg: state => state.successMsg,
}

export const actions = {
    async getCompaniesData({ commit }, params = {}) {
        const { page = 1, perPage = 10, ...filterParams } = params;

        commit('SET_LOADING', true)
        try {
            // Note: axios interceptor already returns response.data, so response is the actual data
            const responseData = await axiosInstance({ requiresAuth: true }).get(`/agent/companies`, {
                params: { page, perPage: perPage, ...filterParams }
            });

            let apiData = responseData;

            // Handle wrapped responses: { success: true, data: { data: [...], meta: {...} } }
            if (responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data) && responseData.data.data) {
                apiData = responseData.data;
            }

            let companiesData;
            let paginationData = {
                currentPage: page,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: perPage,
                hasNext: false,
                hasPrev: false
            };

            // Check for different pagination response structures
            if (apiData.data && Array.isArray(apiData.data) && apiData.meta) {
                // Standard paginated response: { data: [...], meta: {...} }
                companiesData = apiData.data;

                // Extract pagination from meta if present
                const meta = apiData.meta;
                
                paginationData = {
                    currentPage: parseInt(meta.current_page) || page,
                    totalPages: parseInt(meta.last_page) || 1,
                    totalItems: parseInt(meta.total) || companiesData.length,
                    itemsPerPage: parseInt(meta.per_page) || perPage,
                    hasNext: meta.current_page < meta.last_page,
                    hasPrev: meta.current_page > 1
                };
            } else if (apiData.results && Array.isArray(apiData.results)) {
                // Alternative structure: { results: [...], count: N, next: URL, previous: URL }
                companiesData = apiData.results;

                let totalItems = apiData.count || companiesData.length;
                let itemsPerPage = perPage;
                let totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
                let currentPage = page;
                let hasNext = apiData.next ? currentPage < totalPages : false;
                let hasPrev = apiData.previous ? currentPage > 1 : false;

                // If next/previous not provided, calculate based on pages
                if (!apiData.next && !apiData.previous) {
                    hasNext = currentPage < totalPages;
                    hasPrev = currentPage > 1;
                }

                paginationData = {
                    currentPage,
                    totalPages,
                    totalItems,
                    itemsPerPage,
                    hasNext,
                    hasPrev
                };
            } else if (Array.isArray(apiData)) {
                // Direct array response (no pagination metadata) - treat as single page
                companiesData = apiData;
                paginationData = {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: companiesData.length,
                    itemsPerPage: companiesData.length,
                    hasNext: false,
                    hasPrev: false
                };
            } else {
                // Fallback
                companiesData = apiData || []; paginationData.totalItems = companiesData.length;
            }

            commit('SET_COMPANIES_DATA', companiesData);
            commit('SET_PAGINATION', paginationData);
            commit('SET_COMPANIES_MSG', null);
        } catch (error) {
            commit('SET_COMPANIES_MSG', error.response?.data?.message || 'Failed to fetch companies');
        } finally {
            commit('SET_LOADING', false);
        }
    },
    async getSimplifiedCompaniesData({ commit }) {
        commit('SET_LOADING', true)
        try {
            const response = await axiosInstance({ requiresAuth: true }).get(`/agent/simplified/companies`);

            const responseData = response.data;

            // For simplified endpoint, expect direct array or simple structure
            let companiesData;
            if (Array.isArray(responseData)) {
                companiesData = responseData;
            } else if (responseData && Array.isArray(responseData.data)) {
                companiesData = responseData.data;
            } else {
                companiesData = responseData || [];
            }

            commit('SET_COMPANIES_DATA', companiesData);
            commit('SET_COMPANIES_MSG', null);
        } catch (error) {
            commit('SET_COMPANIES_MSG', error.response?.data?.message || 'Failed to fetch companies');
        } finally {
            commit('SET_LOADING', false);
        }
    },
    async updateCompany({ commit }, updateCompanyData) {
        // Clear previous messages
        commit('SET_ERROR_MSG', null);
        commit('SET_SUCCESS_MSG', null);

        try {
            // Using proper async/await pattern
            const response = await axiosInstance({ requiresAuth: true })
                .patch(`/agent/companies/${updateCompanyData.id}`, updateCompanyData);

            // If we get here, the request was successful
            commit('UPDATE_COMPANY', updateCompanyData);
            commit('SET_SUCCESS_MSG', 'Company updated successfully');

            return Promise.resolve(response); // Return successful response
        } catch (error) {
            // Properly handle the error
            const errorMessage = error.response?.data?.message || 'An error occurred while updating the company';
            commit('SET_ERROR_MSG', errorMessage);

            // Re-throw the error so the component can also handle it if needed
            return Promise.reject(error);
        }
    },
    async deleteCompany({ commit }, companyId) {
        commit('SET_LOADING', true);
        try {
            const response = await axiosInstance({ requiresAuth: true })
                .delete(`/agent/companies/${companyId}`);

            // Remove the company from the local state
            commit('DELETE_COMPANY', companyId);
            commit('SET_COMPANIES_MSG', 'Company deleted successfully');

            return Promise.resolve(response);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while deleting the company';
            commit('SET_COMPANIES_MSG', errorMessage);
            return Promise.reject(error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async createCompany({ commit }, payload) {
        commit('SET_ERROR_MSG', null);
        commit('SET_SUCCESS_MSG', null);
        commit('SET_LOADING', true);
        try {
            let body;
            let headers = {};

            if (payload instanceof FormData) {
                body = payload;
                // Explicitly set multipart/form-data so axios 1.x does NOT JSON-serialize the FormData.
                // The browser's XHR will add the correct boundary automatically.
                headers['Content-Type'] = 'multipart/form-data';
            } else {
                body = payload;
                headers['Content-Type'] = 'application/json';
            }

            const response = await axiosInstance({ requiresAuth: true })
                .post(`/agent/companies`, body, { headers });

            // The interceptor returns response.data; the API wraps the created company in { data: {...} }
            const created = response?.data ?? response;
            commit('ADD_COMPANY', created);
            commit('SET_SUCCESS_MSG', 'Company created successfully');
            return Promise.resolve(created);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while creating the company';
            commit('SET_ERROR_MSG', errorMessage);
            return Promise.reject(error);
        } finally {
            commit('SET_LOADING', false);
        }
    },
}